import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorCode, apiErrorMessage } from '@/api/client'
import { clientReservations as clientReservationsApi, rewards as rewardsApi } from '@/api/endpoints'
import { Alert, HelpTip, Input, Modal, Spinner } from '@/components/ui'
import { cn, formatMoney, getActiveLocale, parseNaive } from '@/lib/utils'
import { useBootstrap } from '@/hooks/useBootstrap'
import type { InstructorReward, RewardBonus } from '@/types/api'
import { rewardTypeLabel } from '@/features/rewards/typeLabel'

/** `instructor_rewards_types.id` of the hand-written bucket - the one type
 *  recalculation never touches. Mirrors `InstructorRewardType.MANUAL`. */
const MANUAL_REWARD_TYPE_ID = 999

interface Props {
  /** The lesson whose reward lines are being edited. */
  clientReservationId: number
  /** The block the lesson sits in - needed to recalculate on demand. */
  instructorReservationId?: number | null
  instructorName?: string | null
  readOnly?: boolean
  /** Zapnuté vlastné bonusy lekcie - drží ich okno rezervácie, aby ich
   *  uloženie formulára neprepísalo starým výberom. */
  bonusIds?: number[]
  onBonusIdsChange?: (ids: number[]) => void
  onClose: () => void
}

/**
 * "Výška odmeny pre inštruktora" - the reward lines one lesson produced.
 *
 * Editing an amount here is not just a correction of this one line: the rate it
 * implies becomes the rate for every later reward of the same type for that
 * instructor (apps/rewards/calculators.py, ``compute_rate``). That is the whole
 * point of the screen, so it is spelled out at the bottom.
 *
 * Rows that have already been paid out are locked - a recalculation will not
 * touch them either.
 */
export default function LessonRewardModal({
  clientReservationId,
  instructorReservationId,
  instructorName,
  readOnly = false,
  bonusIds = [],
  onBonusIdsChange,
  onClose,
}: Props) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  /** Local echo of the inputs so typing does not fight the query cache. */
  const [draft, setDraft] = useState<Record<number, string>>({})
  /** Názov ručnej odmeny - rovnako lokálne, uloží sa pri opustení poľa. */
  const [names, setNames] = useState<Record<number, string>>({})

  const { data, isLoading } = useQuery({
    queryKey: ['rewards', 'lesson', clientReservationId],
    queryFn: () => rewardsApi.list({ client_reservation_id: clientReservationId, limit: 50 }),
  })
  // Odmena za celý deň (vlastné auto, odvoz) nepatrí žiadnej lekcii - visí
  // na bloku inštruktora bez `client_reservation_id`, takže ju filter podľa
  // lekcie nenájde. Ukazuje sa zvlášť, aby nevyzerala ako odmena za lekciu.
  const { data: blockData } = useQuery({
    queryKey: ['rewards', 'block', instructorReservationId],
    queryFn: () => rewardsApi.list({ instructor_reservation_id: instructorReservationId, limit: 50 }),
    enabled: Boolean(instructorReservationId),
  })
  const dayRows = (blockData?.results ?? []).filter((row) => row.client_reservation_id == null)

  useEffect(() => {
    const all = [...(data?.results ?? []), ...(blockData?.results ?? [])]
    if (all.length) {
      setDraft(Object.fromEntries(all.map((row) => [row.id, String(row.reward ?? 0)])))
      setNames(Object.fromEntries(all.map((row) => [row.id, row.description ?? ''])))
    }
  }, [data, blockData])

  /** Potvrdenie pri pätičke po každej uloženej zmene - na pár sekúnd. */
  const [saved, setSaved] = useState(0)
  useEffect(() => {
    if (!saved) return
    const timer = window.setTimeout(() => setSaved(0), 3000)
    return () => window.clearTimeout(timer)
  }, [saved])

  const refresh = () => {
    setError(null)
    setSaved(Date.now())
    void qc.invalidateQueries({ queryKey: ['rewards'] })
    void qc.invalidateQueries({ queryKey: ['calendar'] })
  }

  const save = useMutation({
    mutationFn: ({ id, ...payload }: { id: number; reward?: number; description?: string }) =>
      rewardsApi.update(id, payload),
    onSuccess: refresh,
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const addManual = useMutation({
    mutationFn: (reward: number) =>
      rewardsApi.addManual({ client_reservation_id: clientReservationId, reward }),
    onSuccess: refresh,
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const removeRow = useMutation({
    mutationFn: (id: number) => rewardsApi.remove(id),
    onSuccess: refresh,
    onError: (err) => setError(apiErrorMessage(err)),
  })

  /** Vlastné bonusy - zapnutie sa uloží hneď a odmena bloku sa prepočíta. */
  const toggleBonus = useMutation({
    mutationFn: (id: number) =>
      clientReservationsApi.setBonuses(
        clientReservationId,
        bonusIds.includes(id) ? bonusIds.filter((x) => x !== id) : [...bonusIds, id],
      ),
    onSuccess: (result) => {
      onBonusIdsChange?.(result.bonus_ids)
      refresh()
    },
    onError: (err) =>
      setError(apiErrorCode(err) === 'reservation.bonusPaid' ? t('reservation.bonusPaid') : apiErrorMessage(err)),
  })

  /** Materialise the automatic lines without leaving the modal. */
  const recalculate = useMutation({
    mutationFn: () =>
      rewardsApi.recalculate({ instructor_reservation_id: instructorReservationId ?? undefined }),
    onSuccess: refresh,
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const rows = data?.results ?? []
  const total = rows.reduce((sum, r) => sum + Number.parseFloat(r.reward || '0'), 0)
  const dayTotal = dayRows.reduce((sum, r) => sum + Number.parseFloat(r.reward || '0'), 0)

  // Bonus „za lekciu“ má počet 1 - rozsah v minútach by nedával zmysel.
  const units = (row: InstructorReward) =>
    row.type_name === 'transporte-people'
      ? t('rewards.persons', { count: row.number ?? 0 })
      : row.type_name === 'own-car' || !row.number || (row.type_name === 'custom-bonus' && row.number === 1)
        ? '—'
        : t('rewards.minutes', { count: row.number })

  const renderRow = (row: InstructorReward) => (
      <tr key={row.id}>
        <td>
          {row.type_id === MANUAL_REWARD_TYPE_ID && !readOnly && !row.paid ? (
            // Ručná odmena má vlastný názov („Príplatok za skupinu“…) - prázdny
            // = názov typu.
            <Input
              className="h-7 w-full py-0 text-[13px]"
              maxLength={150}
              placeholder={rewardTypeLabel(t, row)}
              value={names[row.id] ?? ''}
              onChange={(e) => setNames({ ...names, [row.id]: e.target.value })}
              onBlur={() => {
                const next = (names[row.id] ?? '').trim()
                if (next === (row.description ?? '')) return
                save.mutate({ id: row.id, description: next })
              }}
            />
          ) : row.type_id === MANUAL_REWARD_TYPE_ID && row.description ? (
            row.description
          ) : (
            <>
              {rewardTypeLabel(t, row)}
              {row.description ? (
                <div className="text-[11px] text-slate-400">{row.description}</div>
              ) : null}
            </>
          )}
        </td>
        <td className="text-right text-slate-500">
          {units(row)}
        </td>
        <td className="text-right">
          {readOnly || row.paid ? (
            <span className={row.paid ? 'font-medium text-green-700' : ''}>
              {formatMoney(row.reward)}
            </span>
          ) : (
            <Input
              type="number"
              step="0.01"
              min={0}
              className="h-7 w-24 py-0 text-right text-[13px]"
              value={draft[row.id] ?? ''}
              onChange={(e) => setDraft({ ...draft, [row.id]: e.target.value })}
              onBlur={() => {
                const next = Number.parseFloat(draft[row.id] ?? '')
                if (Number.isNaN(next) || next < 0) {
                  setDraft({ ...draft, [row.id]: String(row.reward ?? 0) })
                  return
                }
                if (next === Number(row.reward)) return
                save.mutate({ id: row.id, reward: next })
              }}
            />
          )}
        </td>
        <td>
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-[11px] font-medium',
              row.paid ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500',
            )}
          >
            {row.paid ? t('rewards.isPaid') : t('rewards.isUnpaid')}
          </span>
        </td>
        <td>
          {/* Only hand-written lines can be removed; automatic ones
              would simply come back on the next recalculation.
              Matched by id, not by name: the type is called `manual`
              on a fresh install but `custom` in databases carried over
              from the old system, and renaming a school's row to suit
              the UI would be the wrong way round. */}
          {!readOnly && !row.paid && row.type_id === MANUAL_REWARD_TYPE_ID ? (
            <button
              type="button"
              className="text-slate-400 hover:text-brand-600"
              title={t('global.delete')}
              onClick={() => removeRow.mutate(row.id)}
            >
              <Trash2 size={14} />
            </button>
          ) : null}
        </td>
      </tr>
  )

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        instructorName
          ? `${t('reservation.rewards')} — ${instructorName}`
          : t('reservation.rewards')
      }
      footer={
        <>
          {saved ? (
            <span className="mr-auto inline-flex items-center gap-1 self-center text-sm text-green-700" role="status">
              <Check size={16} />
              {t('rewards.changesSaved')}
            </span>
          ) : null}
          {!readOnly && instructorReservationId ? (
            <button
              type="button"
              className="btn-secondary"
              disabled={recalculate.isPending}
              onClick={() => recalculate.mutate()}
              title={t('rewards.recalculateLessonHint')}
            >
              {recalculate.isPending ? <Spinner /> : <RefreshCw size={15} />}
              {t('rewards.recalculateLesson')}
            </button>
          ) : null}
          <button type="button" className="btn-primary" onClick={onClose}>
            {t('global.close')}
          </button>
        </>
      }
    >
      {error ? (
        <div className="mb-3">
          <Alert variant="error">{error}</Alert>
        </div>
      ) : null}

      <SectionTitle title={t('rewards.forReservation')}>{t('rewards.overrideHint')}</SectionTitle>

      {isLoading ? (
        <div className="flex h-24 items-center justify-center text-slate-400">
          <Spinner className="h-5 w-5" />
        </div>
      ) : rows.length === 0 && readOnly ? (
        <Alert variant="info">{t('rewards.noneForLesson')}</Alert>
      ) : (
        <>
        {rows.length === 0 ? (
          <div className="mb-2">
            <Alert variant="info">{t('rewards.noneForLesson')}</Alert>
          </div>
        ) : null}
        <table className="table-base table-fixed [&_tbody_td]:py-1 [&_thead_th]:py-1">
          <colgroup>
            <col />
            <col className="w-24" />
            <col className="w-32" />
            <col className="w-28" />
            <col className="w-10" />
          </colgroup>
          <thead>
            <tr>
              <th>{t('rewards.type')}</th>
              <th className="text-right">{t('rewards.units')}</th>
              <th className="text-right">{t('rewards.amount')}</th>
              <th>{t('rewards.paid')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map(renderRow)}
            {!readOnly ? (
              <tr>
                <td colSpan={5} className="!py-0.5">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[13px] font-medium text-brand-600 hover:bg-brand-50 disabled:opacity-40"
                    disabled={addManual.isPending}
                    onClick={() => addManual.mutate(0)}
                  >
                    {addManual.isPending ? <Spinner /> : <Plus size={14} />}
                    {t('payments.add')}
                  </button>
                </td>
              </tr>
            ) : null}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-300 font-semibold">
              <td colSpan={2} className="px-2.5 py-1 text-right">
                {t('global.total')}
              </td>
              <td className="px-2.5 py-1 text-right">{formatMoney(total)}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
        </>
      )}

      <BonusToggles
        activeIds={bonusIds}
        disabled={readOnly || toggleBonus.isPending}
        pendingId={toggleBonus.isPending ? toggleBonus.variables : null}
        onToggle={(id) => toggleBonus.mutate(id)}
      />

      {dayRows.length ? (
        <div className="mt-3 border-t border-slate-200 pt-2">
          <SectionTitle title={t('rewards.wholeDay')}>{t('rewards.wholeDayHint')}</SectionTitle>
          <table className="table-base table-fixed [&_tbody_td]:py-1 [&_thead_th]:py-1">
          <colgroup>
            <col />
            <col className="w-24" />
            <col className="w-32" />
            <col className="w-28" />
            <col className="w-10" />
          </colgroup>
            <tbody>{dayRows.map(renderRow)}</tbody>
            <tfoot>
              <tr className="border-t border-slate-300 font-semibold">
                <td colSpan={2} className="px-2.5 py-1 text-right">
                  {t('global.total')}
                </td>
                <td className="px-2.5 py-1 text-right">{formatMoney(dayTotal)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      ) : null}

      <RewardHistory
        clientReservationId={clientReservationId}
        instructorReservationId={instructorReservationId ?? null}
      />
    </Modal>
  )
}

/** Ako sa výška odmeny menila v čase - zbalené, rozbalí sa na kliknutie. */
function RewardHistory({
  clientReservationId,
  instructorReservationId,
}: {
  clientReservationId: number
  instructorReservationId: number | null
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { data = [] } = useQuery({
    queryKey: ['rewards', 'history', clientReservationId, instructorReservationId],
    queryFn: () =>
      rewardsApi.history({
        client_reservation_id: clientReservationId,
        instructor_reservation_id: instructorReservationId,
      }),
  })

  const amount = (value: string | null) => (value === null ? '—' : formatMoney(value))

  return (
    <div className="mt-3 border-t border-slate-200 pt-2">
      <div className="flex items-center justify-between">
        <SectionTitle title={t('rewards.history')}>{t('rewards.historyHint')}</SectionTitle>
        {data.length ? (
          <button
            type="button"
            className="rounded-md px-1.5 py-0.5 text-[13px] font-medium text-brand-600 hover:bg-brand-50"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            {open ? t('rewards.historyHide') : t('rewards.historyShow', { count: data.length })}
          </button>
        ) : (
          <span className="text-[12px] text-slate-400">{t('rewards.historyNone')}</span>
        )}
      </div>
      {open && data.length ? (
        <table className="table-base table-fixed [&_tbody_td]:py-1">
          <colgroup>
            <col className="w-32" />
            <col />
            <col className="w-40" />
            <col className="w-36" />
          </colgroup>
          <tbody>
            {data.map((row) => (
              <tr key={row.id}>
                <td className="text-slate-500">{when(row.created)}</td>
                <td>
                  {rewardTypeLabel(t, row)}
                  {row.whole_day ? <span className="ml-1 text-[11px] text-slate-400">({t('rewards.wholeDayShort')})</span> : null}
                </td>
                <td className="text-right tabular-nums">
                  <span className="text-slate-400">{amount(row.old_reward)}</span> → <span className="font-medium">{amount(row.new_reward)}</span>
                </td>
                <td className="text-slate-500">
                  {t(`rewards.reasons.${row.reason}`)}
                  {row.user ? <div className="truncate text-[11px] text-slate-400">{row.user}</div> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  )
}

function when(value: string) {
  const d = parseNaive(value)
  return d
    ? d.toLocaleString(getActiveLocale(), { day: 'numeric', month: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit' })
    : ''
}

/** Nadpis sekcie s otáznikom - vysvetlenie až po kliknutí. */
export function SectionTitle({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-1 flex items-center gap-1 text-xs font-medium uppercase text-slate-500">
      {title}
      <HelpTip label={title}>{children}</HelpTip>
    </div>
  )
}

/**
 * Vlastné bonusy inštruktora (Nastavenia → Odmeny pre inštruktora) - len
 * v detaile odmeny. Bonus sa zapína tlačidlom; zapnutý sa pripočíta
 * k celkovej odmene inštruktora lekcie. Bez definovaných bonusov sa sekcia
 * nezobrazí.
 */
export function BonusToggles({
  activeIds,
  onToggle,
  disabled,
  pendingId,
}: {
  activeIds: number[]
  onToggle: (id: number) => void
  disabled?: boolean
  pendingId?: number | null
}) {
  const { t } = useTranslation()
  const { data: boot } = useBootstrap()
  const bonuses: RewardBonus[] = boot?.reward_bonuses ?? []
  if (!bonuses.length) return null
  return (
    <div className="mt-3 border-t border-slate-200 pt-2">
      <SectionTitle title={t('rewards.bonuses')}>{t('rewards.bonusesHint')}</SectionTitle>
      <div className="flex flex-wrap gap-2">
        {bonuses.map((bonus) => {
          const active = activeIds.includes(bonus.id)
          return (
            <button
              key={bonus.id}
              type="button"
              aria-pressed={active}
              disabled={disabled}
              onClick={() => onToggle(bonus.id)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[13px] font-medium transition-colors disabled:opacity-60',
                active
                  ? 'border-green-600 bg-green-600 text-white hover:bg-green-700'
                  : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400 hover:bg-slate-50',
              )}
            >
              {pendingId === bonus.id ? <Spinner /> : active ? <Check size={14} /> : null}
              {bonus.name}
              {bonus.reward !== undefined ? (
                <span className={active ? 'text-green-100' : 'text-slate-400'}>
                  +{formatMoney(bonus.reward)}
                  {bonus.per === 'hour' ? ` / ${t('reservation.bonusPerHourShort')}` : ''}
                </span>
              ) : null}
              <span className="sr-only">{active ? t('rewards.bonusActive') : t('rewards.bonusInactive')}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
